import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

// Chave de criptografia - em produção, isso deve vir de uma variável de ambiente
// ou ser gerada de forma mais segura
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'armazem-somar-secret-key-2024';

/**
 * Criptografa e salva um valor em um cookie
 * @param {string} name - Nome do cookie
 * @param {any} value - Valor a ser salvo (será convertido para JSON)
 * @param {object} options - Opções do cookie (expires, path, etc)
 */
export const setEncryptedCookie = (name, value, options = {}) => {
  try {
    // Converte o valor para string JSON
    const jsonValue = JSON.stringify(value);
    
    // Criptografa o valor
    const encrypted = CryptoJS.AES.encrypt(jsonValue, ENCRYPTION_KEY).toString();
    
    // Define opções padrão
    // Verifica se está em HTTPS
    const isSecure = window.location.protocol === 'https:';
    
    // Usa 'lax' que funciona tanto em HTTP quanto HTTPS e é mais compatível
    const defaultOptions = {
      expires: 7, // 7 dias
      path: '/',
      sameSite: 'lax', // 'lax' funciona em HTTP e HTTPS, mais compatível com navegadores antigos
      secure: isSecure // Apenas HTTPS se realmente estiver em HTTPS
    };
    
    // Salva o cookie criptografado
    Cookies.set(name, encrypted, { ...defaultOptions, ...options });
    
    // Debug: verifica se o cookie foi salvo
    const saved = Cookies.get(name);
    if (!saved) {
      console.warn(`⚠️ Cookie ${name} não foi salvo! Verifique as configurações do navegador.`);
    } else {
      console.log(`✅ Cookie ${name} salvo com sucesso`);
    }
  } catch (error) {
    console.error('Erro ao salvar cookie criptografado:', error);
    throw error;
  }
};

/**
 * Lê e descriptografa um valor de um cookie
 * @param {string} name - Nome do cookie
 * @returns {any|null} - Valor descriptografado ou null se não existir
 */
export const getEncryptedCookie = (name) => {
  try {
    const encrypted = Cookies.get(name);
    
    if (!encrypted) {
      return null;
    }
    
    // Descriptografa o valor
    const decrypted = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY);
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      // Se a descriptografia falhar, remove o cookie inválido
      removeEncryptedCookie(name);
      return null;
    }
    
    // Converte de JSON para objeto
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('Erro ao ler cookie criptografado:', error);
    // Remove o cookie se houver erro na descriptografia
    removeEncryptedCookie(name);
    return null;
  }
};

/**
 * Remove um cookie criptografado
 * @param {string} name - Nome do cookie
 * @param {object} options - Opções do cookie (path, etc)
 */
export const removeEncryptedCookie = (name, options = {}) => {
  const defaultOptions = {
    path: '/'
  };
  
  Cookies.remove(name, { ...defaultOptions, ...options });
};

/**
 * Salva uma string simples em um cookie criptografado
 * @param {string} name - Nome do cookie
 * @param {string} value - Valor string a ser salvo
 * @param {object} options - Opções do cookie
 */
export const setEncryptedCookieString = (name, value, options = {}) => {
  try {
    // Criptografa o valor diretamente (sem JSON.stringify)
    const encrypted = CryptoJS.AES.encrypt(value, ENCRYPTION_KEY).toString();
    
    // Define opções padrão
    // Verifica se está em HTTPS
    const isSecure = window.location.protocol === 'https:';
    
    // Usa 'lax' que funciona tanto em HTTP quanto HTTPS e é mais compatível
    const defaultOptions = {
      expires: 7, // 7 dias
      path: '/',
      sameSite: 'lax', // 'lax' funciona em HTTP e HTTPS, mais compatível com navegadores antigos
      secure: isSecure // Apenas HTTPS se realmente estiver em HTTPS
    };
    
    Cookies.set(name, encrypted, { ...defaultOptions, ...options });
  } catch (error) {
    console.error('Erro ao salvar cookie criptografado:', error);
    throw error;
  }
};

/**
 * Lê uma string simples de um cookie criptografado
 * @param {string} name - Nome do cookie
 * @returns {string|null} - Valor descriptografado ou null
 */
export const getEncryptedCookieString = (name) => {
  try {
    const encrypted = Cookies.get(name);
    
    if (!encrypted) {
      return null;
    }
    
    const decrypted = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY);
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      removeEncryptedCookie(name);
      return null;
    }
    
    return decryptedString;
  } catch (error) {
    console.error('Erro ao ler cookie criptografado:', error);
    removeEncryptedCookie(name);
    return null;
  }
};

// ============================================================================
// Funções helper específicas para cada tipo de dado
// ============================================================================

/**
 * Gerencia o token JWT em cookies criptografados
 */
export const tokenStorage = {
  set: (token) => setEncryptedCookieString('token', token, { expires: 1 }), // 1 dia para token
  get: () => getEncryptedCookieString('token'),
  remove: () => removeEncryptedCookie('token')
};

/**
 * Gerencia os dados do usuário em cookies criptografados
 */
export const userDataStorage = {
  set: (userData) => setEncryptedCookie('userData', userData),
  get: () => getEncryptedCookie('userData'),
  remove: () => removeEncryptedCookie('userData')
};

/**
 * Gerencia o username em cookies criptografados
 */
export const usernameStorage = {
  set: (username) => setEncryptedCookieString('username', username),
  get: () => getEncryptedCookieString('username'),
  remove: () => removeEncryptedCookie('username')
};

/**
 * Gerencia o flag pinVerified em cookies criptografados
 */
export const pinVerifiedStorage = {
  set: (verified) => setEncryptedCookieString('pinVerified', verified ? 'true' : 'false'),
  get: () => {
    const value = getEncryptedCookieString('pinVerified');
    return value === 'true';
  },
  remove: () => removeEncryptedCookie('pinVerified')
};

/**
 * Gerencia as permissões do usuário em cookies criptografados
 */
export const permissionsStorage = {
  set: (permissions) => setEncryptedCookie('userPermissions', permissions),
  get: () => getEncryptedCookie('userPermissions'),
  remove: () => removeEncryptedCookie('userPermissions')
};

/**
 * Limpa todos os dados de autenticação
 */
export const clearAllAuthData = () => {
  tokenStorage.remove();
  userDataStorage.remove();
  usernameStorage.remove();
  pinVerifiedStorage.remove();
  permissionsStorage.remove();
};

